import { api } from '../core';
import { CASE_METADATA_TAG } from '../constants';
import { setCaseMetadata, setFullProductType } from '@/store';

export interface CaseUserInfo {
  userId: string;
  userName: string;
}

export interface AllegationDocumentInfo {
  allegationDocumentId: string;
  allegationDocumentName: string;
}

export interface CloneOfInfo {
  caseReference: string;
  caseId: string;
}

export interface CaseMetadata {
  assignedTo: CaseUserInfo;
  caseId: string;
  caseReference: string;
  statusUpdated: string;
  createdBy: CaseUserInfo;
  dueDate: string;
  isUrgent: boolean;
  status: string;
  gRef: string;
  paginationProduct: string;
  allegationDocuments: AllegationDocumentInfo[];
  cloneOf: CloneOfInfo | null;
  // Optional instructing company id (MLS org only). `null` / omitted
  // when the case has no instructing company assigned.
  instructingCompanyId?: string | null;
  // Human-readable name for the currently-assigned instructing company.
  // Surfaced so the Create/Edit case sidebar can render the saved
  // selection in the dropdown even before — or independently of —
  // the list-instructing-companies query resolving (and even if that
  // list no longer includes this id).
  instructingCompanyName?: string | null;
}

/**
 * Client-detail fields surfaced on the chronology page (MLS only) via the
 * "Details" modal. The BE returns this sibling to `info` rather than nested
 * inside it; `null` means the case has nothing saved yet.
 *
 * Note: the modal also shows an "Additional reference" field, but that maps
 * to the existing top-level `info.gRef` on CaseMetadata — not a property on
 * this object. It's sent on case/edit as the top-level `gRef`, not nested.
 */
export interface AdditionalInfo {
  clientName: string | null;
  clientDob: string | null; // ISO date, e.g. '1991-01-18'
  pastMedicalHistory: string | null;
  surgicalProcedures: string | null;
}

export interface GetCaseResponse {
  fullProductType: string;
  info: CaseMetadata;
  additionalInfo: AdditionalInfo | null;
}

export interface GetCaseProps {
  abortController?: AbortController;
  caseId: string;
}

const getCaseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getCase: builder.query<GetCaseResponse, GetCaseProps>({
      query: ({ abortController = new AbortController(), caseId }: GetCaseProps) => ({
        url: `case?case_id=${caseId}`,
        method: 'GET',
        signal: abortController.signal,
      }),
      providesTags: (_result, _err, { caseId }) => [{ type: CASE_METADATA_TAG, id: caseId }],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data?.fullProductType) {
            dispatch(setFullProductType(data.fullProductType));
          }
          if (data?.info) {
            dispatch(setCaseMetadata(data.info));
          }
        } catch (error) {
          if (error instanceof Error && error.name === 'AbortError') {
            console.log('Request was aborted');
          } else {
            console.error('Error in getCase in onQueryStarted:', error);
          }
        }
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

export const { useGetCaseQuery, useLazyGetCaseQuery } = getCaseApi;
